import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { format } from "date-fns";
import { FaHistory, FaCalendarAlt, FaFilter, FaMapMarkerAlt, FaLaptop, FaGlobe, FaUser, FaSearch, FaSync } from "react-icons/fa";

import CardComponent from "../../../components/cards/CardComponent";
import Header from "../../../components/Header";
import Layout from "../../../layout/Layout";
import { getHeaders } from "../../../utility/getHeader";
import fetchData from "../../../functions/fetchData";
import "./LoginHistory.css";

export default function LoginHistory() {
  const currentDate = format(new Date(), "yyyy-MM-dd");
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [filters, setFilters] = useState({
    startDate: currentDate,
    endDate: currentDate,
    username: "",
    deviceType: "",
    loginStatus: ""
  });

  // Fetch login history data using fetchData function
  const fetchLoginHistory = async (page = 1) => {
    setLoading(true);
    try {
      // Build where parameters
      const whereParams = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        page: page,
        limit: rowsPerPage
      };

      // Add optional filters only if they have values
      if (filters.username) {
        whereParams.username = filters.username;
      }
      if (filters.deviceType) {
        whereParams.deviceType = filters.deviceType;
      }
      if (filters.loginStatus) {
        whereParams.loginStatus = filters.loginStatus;
      }

      // Fetch data using fetchData function
      const data = await fetchData('login_history', null, 'id', {});

      if (data && data.length > 0) {
        setLoginHistory(data);
        setTotalRecords(data.length);
        setTotalPages(Math.ceil(data.length / rowsPerPage));
        setCurrentPage(page);
        toast.success(`Loaded ${data.length} records`);
      } else {
        setLoginHistory([]);
        setTotalRecords(0);
        setTotalPages(1);
        toast.info("No records found for the selected filters");
      }
    } catch (error) {
      console.error("Error fetching login history:", error);
      toast.error("Failed to load login history");
      setLoginHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchLoginHistory(1);
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search/filter button
  const handleSearch = () => {
    fetchLoginHistory(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchLoginHistory(newPage);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
    fetchLoginHistory(1);
  };

  // Reset filters
  const handleReset = () => {
    setFilters({
      startDate: currentDate,
      endDate: currentDate,
      username: "",
      deviceType: "",
      loginStatus: ""
    });
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm:ss");
    } catch {
      return dateString;
    }
  };

  // Pagination component
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page button
    if (currentPage > 1) {
      pages.push(
        <button
          key="first"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          «
        </button>
      );
      pages.push(
        <button
          key="prev"
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          ‹
        </button>
      );
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page button
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          ›
        </button>
      );
      pages.push(
        <button
          key="last"
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          »
        </button>
      );
    }

    return pages;
  };

  return (
    <>
      <Layout>
        <Header title="Login History" />
        <ToastContainer />

        <div className="login-history-container">
          {/* Filters Card */}
          <CardComponent
            title="Filters"
            headerColor="#667eea"
            pull="left"
            bodyClass="panel-body p-3"
          >
            <div className="filter-grid">
              <div className="filter-item">
                <label className="filter-label">
                  <FaCalendarAlt className="icon" /> Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="filter-input"
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">
                  <FaCalendarAlt className="icon" /> End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="filter-input"
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">
                  <FaUser className="icon" /> Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={filters.username}
                  onChange={handleFilterChange}
                  placeholder="Search by username"
                  className="filter-input"
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">
                  <FaLaptop className="icon" /> Device Type
                </label>
                <select
                  name="deviceType"
                  value={filters.deviceType}
                  onChange={handleFilterChange}
                  className="filter-input"
                >
                  <option value="">All Devices</option>
                  <option value="Desktop">Desktop</option>
                  <option value="Mobile">Mobile</option>
                  <option value="Tablet">Tablet</option>
                </select>
              </div>

              <div className="filter-item">
                <label className="filter-label">
                  <FaFilter className="icon" /> Status
                </label>
                <select
                  name="loginStatus"
                  value={filters.loginStatus}
                  onChange={handleFilterChange}
                  className="filter-input"
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              <div className="filter-item filter-actions">
                <button className="btn-search" onClick={handleSearch}>
                  <FaSearch className="icon" /> Search
                </button>
                <button className="btn-reset" onClick={handleReset}>
                  <FaSync className="icon" /> Reset
                </button>
              </div>
            </div>
          </CardComponent>

          {/* Results Card */}
          <CardComponent
            title={`Login History Records (${totalRecords} total)`}
            headerColor="#17a2b8"
            pull="left"
            bodyClass="panel-body p-0"
          >
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading login history...</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th className="col-id">#</th>
                        <th className="col-user">
                          <FaUser className="icon" /> User
                        </th>
                        <th className="col-datetime">
                          <FaCalendarAlt className="icon" /> Date & Time
                        </th>
                        <th className="col-device">
                          <FaLaptop className="icon" /> Device
                        </th>
                        <th className="col-browser">
                          <FaGlobe className="icon" /> Browser
                        </th>
                        <th className="col-ip">IP Address</th>
                        <th className="col-location">
                          <FaMapMarkerAlt className="icon" /> Location
                        </th>
                        <th className="col-status">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginHistory.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="no-data">
                            <FaHistory size={40} />
                            <p>No login history found</p>
                            <small>Try adjusting your filters</small>
                          </td>
                        </tr>
                      ) : (
                        loginHistory.map((record, index) => (
                          <tr key={record.id || index}>
                            <td className="col-id">
                              {(currentPage - 1) * rowsPerPage + index + 1}
                            </td>
                            <td className="col-user">
                              <div className="user-info">
                                <strong>{record.username}</strong>
                                <small>ID: {record.user_id}</small>
                              </div>
                            </td>
                            <td className="col-datetime">
                              {formatDateTime(record.login_datetime)}
                            </td>
                            <td className="col-device">
                              <div className="device-info">
                                <span className="device-type">
                                  {record.device_type || "Unknown"}
                                </span>
                                <small>{record.device_name || "N/A"}</small>
                              </div>
                            </td>
                            <td className="col-browser">{record.browser || "N/A"}</td>
                            <td className="col-ip">
                              <code>{record.ip_address || "N/A"}</code>
                            </td>
                            <td className="col-location">
                              {record.location_address ? (
                                <div className="location-info">
                                  <span title={record.location_address}>
                                    {record.location_address.substring(0, 30)}...
                                  </span>
                                  {record.map_link && (
                                    <a
                                      href={record.map_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="map-link"
                                    >
                                      <FaMapMarkerAlt />
                                    </a>
                                  )}
                                </div>
                              ) : record.latitude && record.longitude ? (
                                <span className="coordinates">
                                  {record.latitude}, {record.longitude}
                                </span>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td className="col-status">
                              <span
                                className={`status-badge ${
                                  record.login_status === "success"
                                    ? "status-success"
                                    : "status-failed"
                                }`}
                              >
                                {record.login_status || "Unknown"}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {loginHistory.length > 0 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>Show</span>
                        <select
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                          style={{
                            padding: '5px 10px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '14px'
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                      </div>
                      <div>
                        Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                        {Math.min(currentPage * rowsPerPage, totalRecords)} of{" "}
                        {totalRecords} entries
                      </div>
                    </div>
                    <div className="pagination-controls">
                      {renderPagination()}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardComponent>
        </div>
      </Layout>
    </>
  );
}
